import { useEffect, useState } from 'react';
import type { GroupData } from './types';

type State =
  | { status: 'loading' }
  | { status: 'refreshing' }
  | { status: 'ready'; data: GroupData }
  | { status: 'error'; message: string };

/** How long to keep retrying while the server reports it is still refreshing. */
const RETRY_DELAY_MS = 3000;
const MAX_RETRIES = 20;

/**
 * Loads the generated dataset at runtime rather than baking it into the bundle.
 * The refresh job rewrites data/groups.json in place, so fresh group data never
 * requires a rebuild or redeploy.
 *
 * A 503 is not an error here, it is a state: a freshly deployed container
 * answers that way until its first fetch from CCB lands, in preference to
 * serving the committed sample, which is a frozen copy of a previous term.
 * Retrying quietly means a visitor who arrives mid-deploy sees "updating" for a
 * moment and then the real list, rather than a dead end or the wrong groups.
 */
export function useGroupData(): State {
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const load = async (attempt: number): Promise<void> => {
      try {
        const response = await fetch('/data/groups.json', {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });

        if (response.status === 503 && attempt < MAX_RETRIES) {
          if (cancelled) return;
          setState({ status: 'refreshing' });
          timer = setTimeout(() => void load(attempt + 1), RETRY_DELAY_MS);
          return;
        }
        if (!response.ok) throw new Error(`Could not load group data (HTTP ${response.status})`);

        const data = (await response.json()) as GroupData;
        if (cancelled) return;
        if (!data?.groups || !Array.isArray(data.groups)) {
          throw new Error('Group data is malformed');
        }
        setState({ status: 'ready', data });
      } catch (error: unknown) {
        if (cancelled) return;
        setState({
          status: 'error',
          message: error instanceof Error ? error.message : 'Could not load group data',
        });
      }
    };

    void load(0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  return state;
}
